import React, { useState, useRef, useEffect } from 'react';
import { Menu, Search, Sun, Moon, LogIn, LogOut, User, ChevronDown, Mail, Phone, Lock, ArrowLeft } from 'lucide-react';
import useAuthStore from '../../game/authStore';
import lanterna from '../../assets/lanterna.png';

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  );
}

function LoginMenu({ onSocialLogin, loading }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('options');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const { login, register, error } = useAuthStore();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setView('options');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSocial = async (provider) => {
    setOpen(false);
    setView('options');
    await onSocialLogin(provider);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      const ok = await register(email, phone, password, displayName || undefined);
      if (ok) {
        useAuthStore.getState().logout();
        setPhone('');
        setPassword('');
        setDisplayName('');
        setMode('login');
        useAuthStore.setState({ error: null });
      }
    } else {
      const ok = await login(email, password);
      if (ok) {
        setOpen(false);
        setView('options');
        setEmail('');
        setPassword('');
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-[var(--accent)] text-white hover:opacity-90 transition-all active:scale-95"
      >
        <LogIn size={16} />
        <span className="hidden sm:inline">Entrar</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-xl py-2 z-50">
          {view === 'options' ? (
            <>
              <p className="px-3 pb-2 text-xs text-[var(--muted)] font-medium">
                Entrar com rede social
              </p>
              <button
                onClick={() => handleSocial('facebook')}
                disabled={loading}
                className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-[var(--accent-soft)] transition-colors text-[var(--text)] disabled:opacity-50"
              >
                <span className="text-[#1877F2]"><FacebookIcon /></span>
                Facebook / Instagram
              </button>
              <button
                onClick={() => handleSocial('twitter')}
                disabled={loading}
                className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-[var(--accent-soft)] transition-colors text-[var(--text)] disabled:opacity-50"
              >
                <span className="text-[var(--text)]"><XIcon /></span>
                X (Twitter)
              </button>
              <button
                onClick={() => handleSocial('tiktok')}
                disabled={loading}
                className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-[var(--accent-soft)] transition-colors text-[var(--text)] disabled:opacity-50"
              >
                <span className="text-[var(--text)]"><TikTokIcon /></span>
                TikTok
              </button>
              <div className="my-2 mx-3 border-t border-[var(--border)]" />
              <button
                onClick={() => { setView('email'); setMode('login'); useAuthStore.setState({ error: null }); }}
                className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-[var(--accent-soft)] transition-colors text-[var(--text)]"
              >
                <Mail size={18} className="text-[var(--accent)]" />
                Email e Telefone
              </button>
            </>
          ) : (
            <form onSubmit={handleEmailSubmit} className="px-3 py-1">
              <div className="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => { setView('options'); useAuthStore.setState({ error: null }); }}
                  className="p-1 rounded-lg hover:bg-[var(--accent-soft)] text-[var(--muted)] transition-colors"
                >
                  <ArrowLeft size={16} />
                </button>
                <p className="text-sm font-medium text-[var(--text)]">
                  {mode === 'login' ? 'Entrar' : 'Criar conta'}
                </p>
              </div>

              {mode === 'register' && (
                <div className="mb-2">
                  <div className="relative">
                    <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                    <input
                      type="text"
                      placeholder="Nome (opcional)"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border border-[var(--border)] bg-[var(--bg)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                    />
                  </div>
                </div>
              )}

              <div className="mb-2">
                <div className="relative">
                  <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                  <input
                    type={mode === 'register' ? 'email' : 'text'}
                    required
                    placeholder={mode === 'register' ? 'seu@email.com' : 'Email ou telefone'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border border-[var(--border)] bg-[var(--bg)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                  />
                </div>
              </div>

              {mode === 'register' && (
                <div className="mb-2">
                  <div className="relative">
                    <Phone size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                    <input
                      type="tel"
                      required
                      placeholder="(11) 99999-9999"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border border-[var(--border)] bg-[var(--bg)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                    />
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="relative">
                  <Lock size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                  <input
                    type="password"
                    required
                    minLength={4}
                    placeholder="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border border-[var(--border)] bg-[var(--bg)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                  />
                </div>
              </div>

              {error && (
                <div className="mb-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-xs text-red-500">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-lg text-sm font-medium bg-[var(--accent)] text-white hover:opacity-90 transition-all disabled:opacity-50"
              >
                {loading ? 'Aguarde...' : mode === 'login' ? 'Entrar' : 'Criar conta'}
              </button>

              <button
                type="button"
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); useAuthStore.setState({ error: null }); }}
                className="w-full mt-2 py-1.5 text-xs text-[var(--muted)] hover:text-[var(--text)] transition-colors"
              >
                {mode === 'login' ? 'Não tem conta? Criar uma' : 'Já tem conta? Entrar'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-[var(--accent-soft)] transition-colors"
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.displayName}
            className="w-7 h-7 rounded-full object-cover border border-[var(--border)]"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[var(--accent-soft)] flex items-center justify-center">
            <User size={14} className="text-[var(--accent)]" />
          </div>
        )}
        <span className="hidden sm:inline text-sm font-medium text-[var(--text)] max-w-[120px] truncate">
          {user.displayName || 'Usuário'}
        </span>
        <ChevronDown size={14} className={`text-[var(--muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b border-[var(--border)]">
            <p className="text-sm font-medium text-[var(--text)] truncate">{user.displayName || 'Usuário'}</p>
            {user.provider && (
              <p className="text-xs text-[var(--muted)] capitalize">via {user.provider}</p>
            )}
          </div>
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 transition-colors"
          >
            <LogOut size={14} />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export default function Topbar({ onMenuClick }) {
  const { user, logout, openOAuthPopup, loading } = useAuthStore();
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  return (
    <div className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--bg)]/80 backdrop-blur-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Menu + Logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg hover:bg-[var(--accent-soft)] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
            >
              <Menu size={20} />
            </button>
            
            <div className="flex items-center gap-2">
              <img 
                src={lanterna} 
                alt="Lanterna" 
                className="h-8 w-auto"
              />
              <span className="hidden sm:inline-block text-lg font-semibold text-[var(--text)]">
                Guia do Povo
              </span>
            </div>
          </div>

          {/* Center: Search */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--muted)]"
              />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-[var(--accent-soft)] text-[var(--muted)] hover:text-[var(--text)] transition-colors"
              aria-label="Alternar tema"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {user ? (
              <UserMenu user={user} onLogout={logout} />
            ) : (
              <LoginMenu onSocialLogin={openOAuthPopup} loading={loading} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
